import React, { useState, useEffect } from "react";
import { twClassNames } from "../helpers/twClassNames";
import { LoaderIcon } from "./Icons.jsx";
import { TooltipContainer } from "./Tooltip.jsx";

export type ButtonType =
    | "primary"
    | "secondary"
    | "tertiary"
    | "dark-primary"
    | "light"
    | "dark"
    | "dark-ghost"
    | "dark-secondary"
    | "dark-tertiary"
    | "red"
    | "red-ghost"
    | "danger"
    | "success"
    | "success-ghost"
    | "reject-ghost"
    | "ghost"
    | "purbo-ghost"
    | "purbo-ghost-selected"
    | "aqua-ghost"
    | "aqua-ghost-selected"
    | "upgrade"
    | "info"
    | "warning"
    | "error";

export type ButtonSize = "xxsmall" | "xsmall" | "small" | "medium" | "large" | "xlarge" | "icon";

export type ButtonShape = "circle" | "square";

export type ButtonProps = {
    id?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    size?: ButtonSize;
    type?: ButtonType;
    htmlType?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
    className?: any;
    loading?: boolean;
    loadingText?: string;
    tabIndex?: number;
    shape?: ButtonShape;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        children,
        disabled = false,
        onClick = () => {},
        size = "medium",
        type = "primary" as ButtonType,
        htmlType = "button",
        className,
        loading,
        loadingText,
        tabIndex,
        shape,
        ...props
    },
    ref
) {
    function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
        // Ignore clicks if button is disabled
        if (disabled || loading) return;
        onClick(e);
    }

    const showOverlaySpinner = loading && !loadingText;
    const showInlineSpinner = loading && loadingText;

    return (
        <button
            type={htmlType}
            className={twClassNames(["btn2", size, type, shape, "relative", className])}
            disabled={disabled || loading}
            onClick={handleClick}
            tabIndex={tabIndex}
            ref={ref}
            aria-busy={loading}
            {...props}
        >
            {showOverlaySpinner && (
                <span className="absolute inset-0 flex items-center justify-center">
                    <LoaderIcon className="h-5 w-5 animate-spin" />
                </span>
            )}
            {showInlineSpinner && <LoaderIcon className="h-5 w-5 animate-spin" />}
            {showOverlaySpinner ? (
                <span className="contents !text-transparent">{children}</span>
            ) : showInlineSpinner ? (
                loadingText
            ) : (
                children
            )}
        </button>
    );
});

Button.displayName = "Button";

export const ButtonForwardedRef = Button;

export default Button;

export function CopyButton({
    textToCopy,
    buttonText,
    type = "secondary" as ButtonType,
    size = "medium" as ButtonSize,
    className
}: {
    textToCopy: string;
    buttonText?: string;
    type?: ButtonType;
    size?: ButtonSize;
    className?: string;
}) {
    const [isCopied, setIsCopied] = useState(false);
    useEffect(() => {
        let timer;
        if (isCopied) {
            timer = setTimeout(() => {
                setIsCopied(false);
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [isCopied]);

    return (
        <>
            {buttonText ? (
                <Button
                    type={type}
                    size={size}
                    onClick={() => {
                        navigator.clipboard.writeText(textToCopy);
                        setIsCopied(true);
                    }}
                    className={className}
                >
                    <i className={isCopied ? "far fa-check text-success" : "far fa-copy"}></i>
                    {isCopied ? "Copied!" : buttonText}
                </Button>
            ) : (
                <TooltipContainer
                    title="Copy"
                    type="titleOnly"
                    customButton={
                        <span>
                            <Button
                                type={type}
                                size={size}
                                onClick={() => {
                                    navigator.clipboard.writeText(textToCopy);
                                    setIsCopied(true);
                                }}
                            >
                                <i
                                    className={
                                        isCopied ? "far fa-check text-success" : "far fa-copy"
                                    }
                                ></i>
                            </Button>
                        </span>
                    }
                />
            )}
        </>
    );
}

export function HoverDeleteButton({
    onClick,
    className,
    ariaLabel,
    type = "danger" as ButtonType
}: {
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    className?: string;
    ariaLabel?: string;
    type?: ButtonType;
}) {
    return (
        <Button
            onClick={onClick}
            type={type}
            size="icon"
            shape="circle"
            className={twClassNames(
                "absolute !rounded-full opacity-0 ring-1 ring-inset ring-white ring-opacity-50 transition duration-75 group-hover:opacity-100",
                className
            )}
            aria-label={ariaLabel}
            title={ariaLabel}
        >
            <i className="fas fa-xmark fa-xs" />
        </Button>
    );
}
